// NotBuilt.tsx — the placeholder for Billing and Adoption.
//
// The design has twelve nav items. Two of them (Billing & Revenue, Adoption &
// Engagement) sit entirely on backend that does not exist — no plan/entitlement
// model, no billing provider, no product usage events. WIRING_REQUIRED.md §3 and
// §4 describe what each would need.
//
// The owner's instruction was to add the nav entries with an explicit
// "requires backend not yet built" state rather than omit them, so the shell
// matches the design. This screen is that state.
//
// It renders NO sample data. The prototype fills these screens with representative
// numbers, which is correct for a design artefact and wrong for a console: an
// operator cannot tell a sample MRR from a real one, and this is the surface where
// suspend and offboard decisions are made.

import React from "react";
import { PT } from "../../platformTheme";
import { PCard, PSection, PBadge } from "./ui";

export interface NotBuiltSpec {
  title: string;
  summary: string;
  wants: string[];
  needs: { what: string; detail: string }[];
  doc: string;
}

export const BILLING: NotBuiltSpec = {
  title: "Billing & Revenue",
  summary:
    "No billing or subscription system is present. MRR, ARR, net revenue retention, average revenue per customer, gross margin and invoices all depend on data the platform does not hold.",
  wants: ["MRR / ARR", "Net revenue retention", "Avg revenue per customer", "Gross margin", "Invoices & dunning"],
  needs: [
    { what: "Plan + entitlement model", detail: "Plan tier, seat entitlement and MRR per customer — or an integration with a billing provider (e.g. Stripe) read for subscriptions and invoices." },
    { what: "Cost inputs for margin", detail: "Gross margin = revenue − (AI cost + cloud allocation). AI cost needs the usage rollup running; cloud allocation needs Azure Cost Management ingestion, which is also absent." },
    { what: "Endpoints", detail: "GET /api/platform/billing/summary, /billing/by-customer, /billing/invoices — none exists." },
  ],
  doc: "WIRING_REQUIRED.md §B.3",
};

export const ADOPTION: NotBuiltSpec = {
  title: "Adoption & Engagement",
  summary:
    "Partly derivable, mostly not. Logins and user counts already exist in health/platform, but the activation funnel and feature-adoption percentages need product usage events that are not captured.",
  wants: ["MAU / WAU / stickiness", "Activation funnel", "Feature adoption", "At-risk customers"],
  needs: [
    { what: "Product usage events", detail: "feature-used, first-contract-created, invite-accepted. Nothing emits these, so the funnel has no source." },
    { what: "Login time series", detail: "health/platform returns logins7d as a single total, not a series. MAU/WAU need per-day login aggregates." },
    { what: "At-risk scoring", detail: "A query over last-active, seat utilisation and usage trend — each of which depends on the two items above." },
  ],
  doc: "WIRING_REQUIRED.md §B.4",
};

export const NotBuilt: React.FC<{ spec: NotBuiltSpec }> = ({ spec }) => (
  <PSection title={spec.title} sub={spec.doc}>
    <PCard style={{ borderColor: PT.brdStrong, background: PT.bgInset }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 11 }}>
        <PBadge tone="warn">Requires backend not yet built</PBadge>
      </div>
      <div style={{ fontSize: 13, color: PT.tx, lineHeight: 1.6, marginBottom: 16, maxWidth: 760 }}>
        {spec.summary}
      </div>

      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.55, textTransform: "uppercase", color: PT.txD, marginBottom: 7 }}>
        The design shows
      </div>
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 18 }}>
        {spec.wants.map((w) => (
          <span key={w} style={{
            fontSize: 11.5, color: PT.txM, background: PT.bg3, border: `1px solid ${PT.brd}`,
            borderRadius: 7, padding: "3px 9px",
          }}>
            {w}
          </span>
        ))}
      </div>

      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.55, textTransform: "uppercase", color: PT.txD, marginBottom: 9 }}>
        What it would take
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 11, maxWidth: 820 }}>
        {spec.needs.map((n) => (
          <div key={n.what} style={{ display: "flex", gap: 11 }}>
            <span style={{ width: 5, borderRadius: 3, background: PT.brdStrong, flex: "none" }} />
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: PT.tx }}>{n.what}</div>
              <div style={{ fontSize: 12, color: PT.txM, lineHeight: 1.55 }}>{n.detail}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 11.5, color: PT.txD, marginTop: 18, borderTop: `1px solid ${PT.brd}`, paddingTop: 11 }}>
        No sample data is shown. The prototype fills this screen with representative numbers, which
        is right for a design artefact and wrong here — a sample figure and a real one are
        indistinguishable on the surface where lifecycle decisions get made.
      </div>
    </PCard>
  </PSection>
);
